import React, { useMemo, useState, useEffect } from 'react';
import {
  useDispatch,
  useIntl,
  useLocation,
  useSelector,
  history,
  getLocale,
} from 'umi';
import { Guide } from '@/pages/home/components/Guide';
import { INTELLIGENCE_GUIDE_HIDE, INTELLIGENCE_GUIDE_BOX_LIST1 } from '@/pages/home/components/GuideModule/GuideIntelligenceBox';
import withDelayWrapper from './DelayWrapper';
import {
  // GUIDE_SYS_INTELLIGENCE,
  GUIDE_TYPE_INTELLIGENCE_COMPETITOR,
} from '@/pages/home/models/guide';
import { storage } from '@/utils/utils';
import { FROM_PREFIX, DEFAULT_MOBILE_GAMEID, DEFAULT_PC_GAMEID, parseUserGuideStep, generateURLByUserGuideStep } from './utils';
import { LANGUAGE_CN } from '@intelligence/_config/config';

import type { IGuideFrom } from './types';

import './index.scss';

import IMG_GENRE_SELECT_EN from './imgs/en/genre-select.png';
import IMG_GENRE_SELECT_CN from './imgs/cn/genre-select.png';
import IMG_DIGITAL_SEGMENT_EN from './imgs/en/digital-segment-select.png';
import IMG_DIGITAL_SEGMENT_CN from './imgs/cn/digital-segment-select.png';
import IMG_PREDICTED_LINE_CHART_EN from './imgs/en/predicted-line-chart.png';
import IMG_PREDICTED_LINE_CHART_CN from './imgs/cn/predicted-line-chart.png';

export const PREDICTED_LINE_CHART_DEMO_WIDTH = 400;
export const PREDICTED_LINE_CHART_DEMO_HEIGHT = 240;

const BUBBLE_MAX_WIDTH = 660;
const BUBBLE_STYLE = {
  maxWidth: BUBBLE_MAX_WIDTH,
};

export const COMPETITOR_GUIDE_CLASSNAMES = {
  ELEMENT1: 'card-item',
  ELEMENT2: 'dbi-user-guide__menu-topcharts',
  ELEMENT2_HIGHLIGHT1: 'dbi-user-guide__menu-storeranks',
  ELEMENT2_HIGHLIGHT2: 'dbi-user-guide__menu-streaming',
  ELEMENT3: 'dbi-user-guide__topcharts-pc-games__filter__genre-select',
  ELEMENT4: 'dbi-user-guide__pc-games-details__performance-card-digital-revenue',
  ELEMENT4_HIGHLIGHT1: 'dbi-user-guide__pc-games-details__tab__status',
  ELEMENT4_HIGHLIGHT2: 'dbi-user-guide__pc-games-details__tab__countries',
  ELEMENT5: 'dbi-user-guide__pc-games-details-stats__filter-segment-select',
  ELEMENT5_HIGHLIGHT1: 'dbi-user-guide__pc-games-details-stats__metric-segment-digital-revenue',
  ELEMENT6: 'dbi-user-guide__predicted-line-chart-demo',
};

const STEP1_PATH = '/search';
const STEP2_PATH = '/intelligence/topCharts/pcGames';
const STEP4_PATH = `/intelligence/topCharts/pcGames/gamesDetails/${DEFAULT_PC_GAMEID}/overview`;
const STEP5_PATH = `/intelligence/topCharts/pcGames/gamesDetails/${DEFAULT_PC_GAMEID}/stats/digitalRevenue`;
const STEP6_PATH = `/intelligence/topCharts/mobileGames/gamesDetails/${DEFAULT_MOBILE_GAMEID}/stats/activeusers`;
const STEP_PATH_LIST = [STEP1_PATH, STEP2_PATH, STEP2_PATH, STEP4_PATH, STEP5_PATH, STEP6_PATH];
const USER_GUIDE_FROMS_CHARS = Array.from({ length: STEP_PATH_LIST.length - 1 })
  .map((item, idx) => `${FROM_PREFIX}_${idx + 1}`);

const GuideCompetitorContext = () => {
  const location: any = useLocation();
  const { pathname, query } = location;
  const { from } = query || {};
  const initialUserGuideStep = parseUserGuideStep(from);

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isCN = locale === LANGUAGE_CN;

  const dispatch = useDispatch();

  const { guideType, userInfo } = useSelector((state: any) => {
    const guideState = state?.guide;
    const homeState = state?.home;

    return {
      guideType: guideState?.guideType,
      userInfo: homeState?.userInfo,
    };
  });

  const userId = userInfo?.openid;

  const [currentGuideStep, setCurrentGuideStep] = useState(-1);

  const steps = useMemo<any[]>(() => {
    return [
      {
        element: () => document.querySelector(`.${COMPETITOR_GUIDE_CLASSNAMES.ELEMENT1}`),
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 1 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_1_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_1_1_desc' }),
        placement: 'bottom',
        bubbleStyle: BUBBLE_STYLE,
      },
      {
        highlightElements: [
          {
            element: document.querySelector(`.${COMPETITOR_GUIDE_CLASSNAMES.ELEMENT2_HIGHLIGHT1}`),
          },
          {
            element: document.querySelector(`.${COMPETITOR_GUIDE_CLASSNAMES.ELEMENT2_HIGHLIGHT2}`),
          },
        ],
        element: () => document.querySelector(`.${COMPETITOR_GUIDE_CLASSNAMES.ELEMENT2}`),
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 1 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_1_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_1_2_desc' }),
        placement: 'right-start',
        bubbleStyle: BUBBLE_STYLE,
      },
      {
        element: () => document.querySelector(`.${COMPETITOR_GUIDE_CLASSNAMES.ELEMENT3}`),
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 1 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_1_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_1_3_desc' }),
        arrowPointAtCenter: false,
        placement: 'left',
        placementOffset: [200, 10],
        bubbleStyle: BUBBLE_STYLE,
        extImage: {
          image: isCN ? IMG_GENRE_SELECT_CN : IMG_GENRE_SELECT_EN,
          placement: 'bottom-start',
          style: {
            marginLeft: -200,
            width: 541,
            height: 411.5,
          },
        },
      },
      {
        highlightElements: [
          {
            element: document.querySelector(`.${COMPETITOR_GUIDE_CLASSNAMES.ELEMENT4_HIGHLIGHT1}`),
          },
          {
            element: document.querySelector(`.${COMPETITOR_GUIDE_CLASSNAMES.ELEMENT4_HIGHLIGHT2}`),
          },
        ],
        element: () => document.querySelector(`.${COMPETITOR_GUIDE_CLASSNAMES.ELEMENT4}`),
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 1 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_1_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_1_4_desc' }),
        placement: 'top',
        bubbleStyle: BUBBLE_STYLE,
      },
      {
        highlightElements: [
          {
            element: document.querySelector(`.${COMPETITOR_GUIDE_CLASSNAMES.ELEMENT5_HIGHLIGHT1}`),
            padding: {
              paddingTop: 10,
              paddingRight: 10,
              paddingBottom: 10,
              paddingLeft: 10,
            },
          },
        ],
        element: () => document.querySelector(`.${COMPETITOR_GUIDE_CLASSNAMES.ELEMENT5}`),
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 1 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_1_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_1_5_desc' }),
        arrowPointAtCenter: false,
        placement: 'left',
        placementOffset: [200, 10],
        bubbleStyle: BUBBLE_STYLE,
        extImage: {
          image: isCN ? IMG_DIGITAL_SEGMENT_CN : IMG_DIGITAL_SEGMENT_EN,
          placement: 'bottom-start',
          style: {
            marginLeft: -198,
            width: 258,
            height: 246,
          },
        },
      },
      {
        element: () => document.querySelector(`.${COMPETITOR_GUIDE_CLASSNAMES.ELEMENT6}`),
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 1 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_1_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_1_6_desc' }),
        placement: 'left',
        bubbleStyle: BUBBLE_STYLE,
        extImage: {
          image: isCN ? IMG_PREDICTED_LINE_CHART_CN : IMG_PREDICTED_LINE_CHART_EN,
          placement: 'left-start',
          style: {
            position: 'relative',
            left: PREDICTED_LINE_CHART_DEMO_WIDTH + 10,
            width: PREDICTED_LINE_CHART_DEMO_WIDTH,
            height: PREDICTED_LINE_CHART_DEMO_HEIGHT,
          },
        },
      },
    ];
  }, [locale]);

  const handleFinished = () => {
    if (userId) {
      let list = storage.getLocalStorageItem(INTELLIGENCE_GUIDE_BOX_LIST1);
      list = Array.isArray(list) ? list : [];
      if (!list.includes(userId)) {
        list.push(userId);
      }
      storage.setLocalStorageItem(INTELLIGENCE_GUIDE_BOX_LIST1, list);
    }
    dispatch({
      type: 'guide/showIntelligenceGuideModule',
      payload: true,
    });
  };

  // const handleCloseStep = () => {
  //   dispatch({
  //     type: 'guide/save',
  //     payload: {
  //       guideSys: GUIDE_SYS_INTELLIGENCE,
  //       guideType: '',
  //     },
  //   });
  // };

  const handleBack = (current: number) => {
    const prevPathname = STEP_PATH_LIST[current];
    if (prevPathname === pathname) {
      const path = generateURLByUserGuideStep(prevPathname, current);
      history.replace(path);
      return;
    }
    if (prevPathname === STEP4_PATH && pathname === STEP5_PATH) {
      const path = generateURLByUserGuideStep(STEP4_PATH, current);
      history.replace(path);
      return;
    }
    history.goBack();
  };

  const handleNext = (current: number) => {
    if (current >= 0) {
      const { pathname } = location;
      const nextPathname = STEP_PATH_LIST[current];
      const routeAction = pathname === nextPathname || (pathname === STEP4_PATH && nextPathname === STEP5_PATH) ? 'replace' : 'push';
      const routePath = generateURLByUserGuideStep(nextPathname, current);
      history[routeAction](routePath);
    }
  };

  const handleCurrentChange = (
    current: number,
    context: { from: IGuideFrom },
  ) => {
    const { from } = context || {};
    setCurrentGuideStep(current);
    if (from === 'back') {
      handleBack(current);
    } else if (from === 'next') {
      handleNext(current);
    } else if (from === 'finish') {
      handleFinished();
    } else if (from === 'cancel') {
      // handleCloseStep();
      handleFinished();
    }
  };

  // 注意下面两个useEffect不要合并到一起，不然会出现：从头部导航进入新手引导，点击到下一步时，关闭按钮无法关闭，而是返回到第一步
  useEffect(() => {
    if (guideType !== GUIDE_TYPE_INTELLIGENCE_COMPETITOR) return;
    if (
      pathname !== STEP1_PATH &&
      !USER_GUIDE_FROMS_CHARS.includes(from)
    ) return;

    setCurrentGuideStep(initialUserGuideStep);
  }, [guideType, pathname, from, initialUserGuideStep]);

  useEffect(() => {
    const list = storage.getLocalStorageItem(INTELLIGENCE_GUIDE_BOX_LIST1);
    const guideHide = storage.getLocalStorageItem(INTELLIGENCE_GUIDE_HIDE);
    if (!userId || list?.includes(userId) || guideHide) return;
    if (
      pathname !== STEP1_PATH &&
      !USER_GUIDE_FROMS_CHARS.includes(from)
    ) return;

    setCurrentGuideStep(initialUserGuideStep);
  }, [userId, pathname, from, initialUserGuideStep]);

  return (
    // finishText 根据中英文调整
    <Guide
      visibleCloseBtn
      hideCancelButton
      showBackButton
      nextButtonTheme="primary"
      backText={formatMessage({ id: 'guide_last_step' })}
      nextText={formatMessage({ id: 'guide_next_step' })}
      finishText={formatMessage({ id: 'guide_more_step' })}
      steps={steps}
      current={currentGuideStep}
      onCurrentChange={handleCurrentChange}
    />
  );
};

const GuideCompetitor = () => {
  const location = useLocation();

  const { guideType } = useSelector((state: any) => {
    const guideState = state?.guide;
    return {
      guideType: guideState?.guideType,
    };
  });

  if (guideType !== GUIDE_TYPE_INTELLIGENCE_COMPETITOR) return null;
  if (!STEP_PATH_LIST.includes(location.pathname)) return null;

  return <GuideCompetitorContext />;
};

export const GuideCompetitorDelay = withDelayWrapper(GuideCompetitor);

export default GuideCompetitor;
